import Controller from '@ember/controller';
import { action, get } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask, enqueueTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import { alias } from 'macro-decorators';
import { ACCESS_CONTROL_INTEGRATIONS } from 'garaje/utils/enums';
import _mapKeys from 'lodash/mapKeys';
import _mapValues from 'lodash/mapValues';

const APP_STORE_AUTO_CHECK_IN_COLLECTION_ROUTE = '/apps/collections/auto-check-in';

export default class WorkplaceSettingsEmployeesRegistrationIndexController extends Controller {
  @service flow;
  @service flashMessages;
  @service state;
  @service localStorage;
  @service currentAdmin;
  @service skinnyLocations;
  @service featureFlags;
  @service store;
  @service workplaceMetrics;
  @service router;

  @tracked isOpen = false;

  @alias('currentAdmin.isGlobalAdmin') isGlobalAdmin;
  @alias('currentAdmin.isLocationAdmin') isLocationAdmin;
  @alias('state.currentLocation') currentLocation;
  @alias('flow.globalEmployeeScreeningFlows') globalEmployeeScreeningFlows;
  @alias('flow.openedModal') openedModal;
  @alias('skinnyLocations.currentCompanyLocations') currentCompanyLocations;
  @alias('skinnyLocations.manageableByCurrentAdmin') locations;
  @alias('flow.currentSkinnyLocation') currentSkinnyLocation;
  @alias('flow.canDisableEmployeeScreeningFlow') canDisable;
  @alias('flow.canSaveEmployeeScreeningFlow') canSave;
  @alias('flow.selectedEmployeeScreeningFlow') selectedEmployeeScreeningFlow;
  @alias('flow.activeEmployeeScreeningFlow') activeEmployeeScreeningFlow;
  @alias('flow.activeOrSelectedEmployeeScreeningFlow') activeOrSelectedEmployeeScreeningFlow;
  @alias('flow.isEmployeeScreeningFlowConfigured') isEmployeeScreeningFlowConfigured;
  @alias('state.workplaceSubscription') subscription;

  get canEditEmployeePropertyMobileCredentialing() {
    return this.model.connectLocationConfiguration?.canEnableEmployeePropertyMobileCredentialing;
  }

  get canEditEmployeePropertyRebadging() {
    if (!this.model.connectLocationConfiguration) return false;

    return this.model.connectLocationConfiguration.canEnableEmployeeRebadging;
  }

  get currentLocationEnabledDesks() {
    return this.state.currentLocationEnabledDesks;
  }

  get isAutoCheckInDisabled() {
    return !this.subscription?.canAccessAutoCheckIn;
  }

  get isAutoCheckInIntegrationInstalled() {
    return Boolean(this.installedAutoCheckInPluginPaths?.length);
  }

  get installedPluginIds() {
    const { pluginInstalls } = this.model;
    const intalledPluginIds = pluginInstalls
      ?.toArray()
      ?.filter((pluginInstall) => pluginInstall.status === 'active')
      ?.map((pluginInstall) => get(pluginInstall, 'plugin.id'));

    return intalledPluginIds;
  }

  get installedAutoCheckInPluginPaths() {
    const installedPluginIds = this.installedPluginIds;

    const AccessControlPluginIdToPath = _mapValues(
      _mapKeys(ACCESS_CONTROL_INTEGRATIONS, (value, _key) => value.pluginId),
      (value, _key) => value.appStorePath,
    );

    const installedAutoCheckInPluginIds = installedPluginIds?.filter((pluginId) =>
      Object.keys(AccessControlPluginIdToPath).includes(pluginId),
    );

    return installedAutoCheckInPluginIds.map((pluginId) => AccessControlPluginIdToPath[pluginId]) ?? [];
  }

  get showBinaryDownloadSetting() {
    return this.featureFlags.isEnabled('binary-download-settings');
  }

  @action
  openModal(modalToOpen) {
    this.openedModal = modalToOpen;
    this.workplaceMetrics.trackEvent('WORKPLACE_SETTINGS_ADD_OR_CHANGE_REGISTRATION_FLOW_BUTTON_CLICKED');
  }

  @action
  async disableFlowForThisLocation() {
    const { flashMessages, currentSkinnyLocation, canDisable } = this;
    if (canDisable) {
      currentSkinnyLocation.employeeScreeningFlow = null;
      await currentSkinnyLocation.save();
      this.isOpen = !this.isOpen;
      flashMessages.showAndHideFlash('success', 'Saved!');
    }
  }

  @action
  async selectFlowForThisLocation() {
    const { flashMessages, currentSkinnyLocation, selectedEmployeeScreeningFlow, activeEmployeeScreeningFlow } = this;
    currentSkinnyLocation.employeeScreeningFlow = selectedEmployeeScreeningFlow;
    await currentSkinnyLocation.save();
    this.isOpen = !this.isOpen;
    flashMessages.showAndHideFlash('success', 'Saved!');
    if (activeEmployeeScreeningFlow) {
      this.openedModal = 'instructions';
    }
  }

  @action
  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.workplaceMetrics.trackEvent('WORKPLACE_SETTINGS_EDIT_EMPLOYEE_REGISTRATION_BUTTON_CLICKED');
  }

  @dropTask
  *newEmployeeScreeningFlow() {
    const { currentCompany } = this.state;
    const employeeScreeningFlow = this.store.createRecord('employee-screening-flow', {
      company: currentCompany,
    });
    yield employeeScreeningFlow.save();
    this.transitionToRoute('workplace.settings.employees.registration.edit', employeeScreeningFlow.id); // eslint-disable-line ember/no-deprecated-router-transition-methods
  }

  @enqueueTask
  *saveUserDocumentRequirementsTask(requirements) {
    const { flashMessages, currentSkinnyLocation } = this;

    currentSkinnyLocation.userDocumentRequirements = requirements;

    yield currentSkinnyLocation.save();

    flashMessages.showAndHideFlash('success', 'Saved!');
  }

  shouldShowWelcomeBanner() {
    let shouldShowBanner = false;
    try {
      let meta = this.localStorage.getItem('workplace_settings_meta');
      meta = meta ? JSON.parse(meta) : [];
      const { visitedDate } = meta.find((s) => s.locationId === this.state.currentLocation.id) || {};
      if (visitedDate) {
        const isRecent = moment.utc().subtract(5, 'days').isBefore(moment.utc(visitedDate));
        shouldShowBanner = isRecent;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
    return shouldShowBanner;
  }

  setMetaInStorage() {
    try {
      let meta = this.localStorage.getItem('workplace_settings_meta');
      meta = meta ? JSON.parse(meta) : [];
      meta = meta ? JSON.parse(meta) : [];
      const { visitedDate } = meta.find((data) => data.locationId === this.state.currentLocation.id) || {};
      if (!visitedDate) {
        this.localStorage.setItem(
          'workplace_settings_meta',
          JSON.stringify([{ locationId: this.state.currentLocation.id, visitedDate: moment.utc().format() }]),
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  @action
  redirectToAppStoreAutoCheckInCollection() {
    this.router.transitionTo(APP_STORE_AUTO_CHECK_IN_COLLECTION_ROUTE);
  }

  @action
  rollbackAll() {
    const { activeEmployeeScreeningFlow } = this;
    this.selectedEmployeeScreeningFlow = activeEmployeeScreeningFlow;
  }
}
